// src/Login.jsx
import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { signInWithEmailAndPassword, signOut } from "firebase/auth";
import { auth } from "./firebase";
import "animate.css";

export default function Login({
  role = "student", // "student" | "staff" | "driver" | "admin"
  title = "Login",
  subtitle = "Email/Password only",
  onLogin, // async (user) => void
}) {
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const roleBadge = useMemo(() => {
    if (role === "admin") return "Admin";
    if (role === "driver") return "Driver";
    if (role === "staff") return "Staff";
    return "Student";
  }, [role]);

  const primaryBtnClass = useMemo(() => {
    if (role === "student") return "btn-glass-student";
    if (role === "staff") return "btn-glass-staff";
    if (role === "driver") return "btn-glass-driver";
    if (role === "admin") return "btn-glass-admin";
    return "btn-glass-driver";
  }, [role]);

  const roleBadgeClass = useMemo(() => {
    if (role === "student") return "role-badge role-badge--student";
    if (role === "staff") return "role-badge role-badge--staff";
    if (role === "driver") return "role-badge role-badge--driver";
    if (role === "admin") return "role-badge role-badge--admin";
    return "role-badge";
  }, [role]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErr("");

    if (!email.trim() || !pw.trim()) {
      setErr("Please enter email and password.");
      return;
    }

    try {
      setLoading(true);
      const cred = await signInWithEmailAndPassword(auth, email.trim(), pw);
      const user = cred.user;
      if (onLogin) await onLogin(user);
    } catch (e2) {
      setErr(e2?.message || "Login failed");
      try {
        await signOut(auth);
      } catch {}
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <style>{`
        .login-page{
          min-height:100vh;
          display:flex;
          align-items:center;
          justify-content:center;
          padding: 44px 16px;
          position:relative;
          overflow:hidden;
        }
        .login-page::before{
          content:"";
          position:absolute;
          inset:-240px;
          background:
            radial-gradient(900px 600px at 18% 12%, rgba(47,123,255,.22), transparent 60%),
            radial-gradient(900px 600px at 82% 18%, rgba(255,77,109,.14), transparent 60%),
            radial-gradient(900px 600px at 50% 92%, rgba(32,201,151,.12), transparent 60%),
            repeating-linear-gradient(
              0deg,
              rgba(255,255,255,.04) 0px,
              rgba(255,255,255,.04) 1px,
              transparent 1px,
              transparent 14px
            );
          opacity:.55;
          pointer-events:none;
        }

        .login-shell{
          position:relative;
          z-index:1;
          width:100%;
          max-width: 520px;
        }

        .login-card{
          border-radius: 22px;
          padding: 22px;
          background: rgba(255,255,255,.10);
          border: 1px solid rgba(255,255,255,.16);
          backdrop-filter: blur(16px);
          -webkit-backdrop-filter: blur(16px);
          box-shadow: 0 18px 50px rgba(0,0,0,.40);
        }

        .login-top{
          display:flex;
          align-items:flex-start;
          justify-content:space-between;
          gap: 12px;
          margin-bottom: 14px;
        }

        .login-title{
          margin:0;
          font-weight: 900;
          color: rgba(255,255,255,.95);
          letter-spacing:.2px;
        }

        .login-sub{
          margin-top: 6px;
          color: rgba(234,240,255,.72);
          font-size: 13px;
          line-height: 1.6;
          font-weight: 800;
        }

        .role-badge{
          padding: 7px 10px;
          border-radius: 12px;
          border: 1px solid rgba(255,255,255,.18);
          background: rgba(255,255,255,.08);
          color: rgba(255,255,255,.92);
          font-weight: 900;
          font-size: 12px;
          letter-spacing: .8px;
          text-transform: uppercase;
          white-space: nowrap;
        }
        .role-badge--student{ border-color: rgba(21,199,119,.55); background: rgba(21,199,119,.14); }
        .role-badge--staff{ border-color: rgba(168,85,247,.60); background: rgba(168,85,247,.14); }
        .role-badge--driver{ border-color: rgba(47,123,255,.60); background: rgba(47,123,255,.14); }
        .role-badge--admin{ border-color: rgba(255,149,0,.65); background: rgba(255,149,0,.14); }

        .divider{
          border-top: 1px solid rgba(255,255,255,.12);
          margin: 14px 0 16px;
        }

        .field-label{
          color: rgba(234,240,255,.72);
          font-size: 12px;
          font-weight: 900;
          margin-bottom: 6px;
          letter-spacing: .5px;
          text-transform: uppercase;
        }

        .auth-input{
          border-radius: 14px !important;
          background: rgba(255,255,255,.10) !important;
          border: 1px solid rgba(255,255,255,.16) !important;
          color: rgba(255,255,255,.92) !important;
          padding: 12px 12px !important;
        }
        .auth-input::placeholder{ color: rgba(234,240,255,.45); }
        .auth-input:focus{
          box-shadow: 0 0 0 .2rem rgba(47,123,255,.20) !important;
          border-color: rgba(47,123,255,.45) !important;
        }

        .pw-row{
          display:flex;
          gap: 10px;
          align-items:center;
        }
        .pw-grow{ flex:1; }

        .pw-toggle{
          border-radius: 14px;
          border: 1px solid rgba(255,255,255,.18);
          background: rgba(255,255,255,.08);
          color: rgba(255,255,255,.9);
          font-weight: 900;
          padding: 10px 12px;
          min-width: 86px;
        }
        .pw-toggle:hover{ background: rgba(255,255,255,.14); }

        .btn-pro{
          border-radius: 14px !important;
          font-weight: 900 !important;
          padding: 12px 14px !important;
        }

        /* ✅ primary buttons */
        .btn-glass-student{
          border: 0 !important;
          color: #071b10 !important;
          background: linear-gradient(135deg, rgba(21,199,119,1), rgba(34,197,94,1)) !important;
          box-shadow: 0 14px 38px rgba(21,199,119,.28);
        }
        .btn-glass-staff{
          border: 0 !important;
          color: #0b0214 !important;
          background: linear-gradient(135deg, rgba(168,85,247,1), rgba(236,72,153,1)) !important;
          box-shadow: 0 14px 38px rgba(168,85,247,.25);
        }
        .btn-glass-driver{
          border: 0 !important;
          color: #fff !important;
          background: linear-gradient(135deg, rgba(47,123,255,1), rgba(106,166,255,1)) !important;
          box-shadow: 0 14px 38px rgba(47,123,255,.30);
        }
        .btn-glass-admin{
          border: 0 !important;
          color: #1a0b00 !important;
          background: linear-gradient(135deg, rgba(255,149,0,1), rgba(255,77,109,1)) !important;
          box-shadow: 0 14px 38px rgba(255,149,0,.22);
        }

        /* ✅ SWITCH BUTTONS: dark + SAME COLOR tint (as you asked) */
        .switch-row{
          display:grid;
          grid-template-columns: repeat(4, minmax(0, 1fr));
          gap: 10px;
        }

        .btn-switch{
          /* set RGB via per-role class */
          --r: 255; --g: 255; --b: 255;

          position: relative;
          width: 100%;
          border-radius: 18px !important;
          font-weight: 900 !important;
          padding: 12px 10px !important;
          min-height: 46px;

          /* dark glass + tinted fill (same color but dark) */
          background:
            linear-gradient(180deg, rgba(255,255,255,.07), rgba(0,0,0,.18)),
            rgba(var(--r), var(--g), var(--b), 0.18) !important;

          border: 1px solid rgba(var(--r), var(--g), var(--b), 0.65) !important;
          color: rgba(255,255,255,.92) !important;
          text-decoration: none !important;

          display:flex;
          align-items:center;
          justify-content:center;
          overflow:hidden;

          box-shadow:
            inset 0 0 0 1px rgba(255,255,255,.06),
            0 14px 34px rgba(0,0,0,.28),
            0 0 0 1px rgba(var(--r), var(--g), var(--b), 0.14);
          transition: transform .15s ease, background .15s ease;
          animation: tintPulse 2.2s ease-in-out infinite;
        }

        /* diagonal tinted glass strip */
        .btn-switch::before{
          content:"";
          position:absolute;
          left:-40%;
          top:0;
          height:100%;
          width: 74%;
          transform: skewX(-18deg);
          background: linear-gradient(
            135deg,
            rgba(var(--r), var(--g), var(--b), 0.55),
            rgba(var(--r), var(--g), var(--b), 0.10)
          );
          opacity: .72;
          pointer-events:none;
        }

        /* subtle shine */
        .btn-switch::after{
          content:"";
          position:absolute;
          inset:0;
          background: linear-gradient(
            120deg,
            transparent 0%,
            rgba(255,255,255,.18) 45%,
            transparent 62%
          );
          transform: translateX(-120%);
          opacity: .0;
          animation: shineMove 3.0s ease-in-out infinite;
          pointer-events:none;
        }

        .btn-switch > span{
          position:relative;
          z-index:2;
        }

        .btn-switch:hover{
          transform: translateY(-1px);
          background:
            linear-gradient(180deg, rgba(255,255,255,.09), rgba(0,0,0,.22)),
            rgba(var(--r), var(--g), var(--b), 0.22) !important;
        }

        @keyframes tintPulse{
          0%{
            box-shadow:
              inset 0 0 0 1px rgba(255,255,255,.06),
              0 14px 34px rgba(0,0,0,.28),
              0 0 0 1px rgba(var(--r), var(--g), var(--b), 0.14);
          }
          50%{
            box-shadow:
              inset 0 0 0 1px rgba(255,255,255,.08),
              0 16px 40px rgba(0,0,0,.30),
              0 0 0 1px rgba(var(--r), var(--g), var(--b), 0.26);
          }
          100%{
            box-shadow:
              inset 0 0 0 1px rgba(255,255,255,.06),
              0 14px 34px rgba(0,0,0,.28),
              0 0 0 1px rgba(var(--r), var(--g), var(--b), 0.14);
          }
        }

        @keyframes shineMove{
          0%{ transform: translateX(-120%); opacity: 0; }
          35%{ opacity: .55; }
          60%{ opacity: .55; }
          100%{ transform: translateX(120%); opacity: 0; }
        }

        /* ✅ same color (dark) per button */
        .btn-switch--student{ --r: 21;  --g: 199; --b: 119; } /* green */
        .btn-switch--staff{   --r: 168; --g: 85;  --b: 247; } /* purple */
        .btn-switch--driver{  --r: 47;  --g: 123; --b: 255; } /* blue */
        .btn-switch--admin{   --r: 255; --g: 149; --b: 0;   } /* orange */

        /* ✅ mobile tighten so 4 fit in one line */
        @media (max-width: 420px){
          .switch-row{ gap: 8px; }
          .btn-switch{
            border-radius: 16px !important;
            min-height: 42px;
            padding: 10px 8px !important;
            font-size: 12.5px;
          }
        }

        .error-box{
          border-radius: 14px;
          padding: 10px 12px;
          border: 1px solid rgba(255,77,109,.35);
          background: rgba(255,77,109,.12);
          color: rgba(255,230,235,.95);
          font-weight: 800;
          font-size: 13px;
        }
      `}</style>

      <div className="login-shell wow animate__animated animate__fadeInUp animate__slow">
        <div className="login-card">
          <div className="login-top">
            <div>
              <h2 className="login-title">{title}</h2>
              <div className="login-sub">{subtitle}</div>
            </div>
            <div className={roleBadgeClass}>{roleBadge}</div>
          </div>

          <div className="divider" />

          {err && <div className="error-box mb-3">{err}</div>}

          <form onSubmit={handleSubmit}>
            <div className="mb-3">
              <div className="field-label">Email</div>
              <input
                className="form-control auth-input"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="name@example.com"
              />
            </div>

            <div className="mb-3">
              <div className="field-label">Password</div>
              <div className="pw-row">
                <div className="pw-grow">
                  <input
                    className="form-control auth-input"
                    type={showPw ? "text" : "password"}
                    autoComplete="current-password"
                    value={pw}
                    onChange={(e) => setPw(e.target.value)}
                    placeholder="Enter your password"
                  />
                </div>

                <button
                  type="button"
                  className="pw-toggle"
                  onClick={() => setShowPw((v) => !v)}
                >
                  {showPw ? "Hide" : "Show"}
                </button>
              </div>
            </div>

            <button
              className={`btn btn-pro w-100 ${primaryBtnClass}`}
              type="submit"
              disabled={loading}
            >
              {loading ? "Signing in..." : "Sign In"}
            </button>
          </form>

          <div className="divider" />

          <div className="switch-row">
            <Link to="/student-login" className="btn-switch btn-pro btn-switch--student">
              <span>Student</span>
            </Link>
            <Link to="/staff-login" className="btn-switch btn-pro btn-switch--staff">
              <span>Staff</span>
            </Link>
            <Link to="/driver-login" className="btn-switch btn-pro btn-switch--driver">
              <span>Driver</span>
            </Link>
            <Link to="/admin-login" className="btn-switch btn-pro btn-switch--admin">
              <span>Admin</span>
            </Link>
          </div>

          <div className="login-sub mt-3">Note: Email/Password only.</div>
        </div>
      </div>
    </div>
  );
}